import { useMemo } from 'react';
import { createSearchParams, useSearchParams } from 'react-router';

/**
 * A hook to manage tab state in URL search params
 * @template T - The type of tab values (string literals)
 * @param tabs - Array of possible tab values
 * @param defaultTab - Optional default tab value (defaults to first tab)
 * @returns Object with current tab value and change handler
 */
export const useTabs = <T extends string>(tabs: readonly T[], defaultTab?: T) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultTabValue = defaultTab || tabs[0];

  const currentTabParam = searchParams.get('tab') as T | null;
  const currentTab = useMemo(() => {
    return currentTabParam && tabs.includes(currentTabParam) ? currentTabParam : defaultTabValue;
  }, [currentTabParam, tabs, defaultTabValue]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSearchParams(createSearchParams({ tab: tabs[newValue] }));
  };

  return {
    tabValue: currentTab,
    tabIndex: tabs.indexOf(currentTab),
    handleTabChange,
  };
};
